import React, { useCallback, useRef, useState } from 'react';
import { Title, usePermissions } from 'react-admin';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    LinearProgress,
    Paper,
    Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { ButtonField, Tag } from '@wk/components-v3-react16';
import { API_URL } from '../config';
import { useT } from '../i18n/useT';

// ─── types ────────────────────────────────────────────────────────────────────

type EventType = 'info' | 'warning' | 'error' | 'created' | 'updated' | 'skipped';

interface LogEntry {
    id: number;
    type: EventType;
    message: string;
}

interface UploadStats {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    errors: number;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const entryIcon = (type: EventType) => {
    switch (type) {
        case 'created':  return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
        case 'updated':  return <InfoIcon fontSize="small" sx={{ color: 'primary.main' }} />;
        case 'skipped':  return <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />;
        case 'error':    return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
        default:         return <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
};

const entryBg = (type: EventType): string => {
    switch (type) {
        case 'created': return '#e8f5e9';
        case 'updated': return '#e3f2fd';
        case 'skipped': return '#fff8e1';
        case 'error':   return '#ffebee';
        default:        return 'transparent';
    }
};

// ─── component ────────────────────────────────────────────────────────────────

export const MassUpload = () => {
    const t = useT();
    const { permissions } = usePermissions();
    const [files, setFiles] = useState<File[]>([]);
    const [running, setRunning] = useState(false);
    const [log, setLog] = useState<LogEntry[]>([]);
    const [stats, setStats] = useState<UploadStats | null>(null);
    const [progress, setProgress] = useState(0);
    const [finished, setFinished] = useState(false);
    const logEndRef = useRef<HTMLDivElement>(null);
    const idRef = useRef(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const appendLog = useCallback((type: EventType, message: string) => {
        setLog(prev => [...prev, { id: ++idRef.current, type, message }]);
        setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(e.target.files ?? []);
        setFiles(selected);
        setLog([]);
        setStats(null);
        setFinished(false);
        setProgress(0);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const dropped = Array.from(e.dataTransfer.files);
        setFiles(dropped);
        setLog([]);
        setStats(null);
        setFinished(false);
        setProgress(0);
    };

    const startUpload = useCallback(async () => {
        if (running || files.length === 0) return;

        const token = localStorage.getItem('token');
        if (!token) {
            appendLog('error', t('massUpload.events.tokenMissing'));
            return;
        }

        setRunning(true);
        setLog([]);
        setStats(null);
        setFinished(false);
        setProgress(0);

        const totals: UploadStats = { total: files.length, created: 0, updated: 0, skipped: 0, errors: 0 };
        appendLog('info', t('massUpload.events.starting', { count: files.length }));

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setProgress(Math.round((i / files.length) * 100));

            const formData = new FormData();
            formData.append('file', file);

            try {
                const resp = await fetch(`${API_URL}/catalog/mass-upload-file`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                });

                const data = await resp.json();
                const status: string = data.status ?? 'error';
                const msg: string = data.message ?? t('massUpload.events.unknownResponse');

                if (status === 'created') {
                    totals.created++;
                    appendLog('created', msg);
                } else if (status === 'updated') {
                    totals.updated++;
                    appendLog('updated', msg);
                } else if (status === 'skipped') {
                    totals.skipped++;
                    appendLog('skipped', msg);
                } else {
                    totals.errors++;
                    appendLog('error', msg);
                }
            } catch (err: any) {
                totals.errors++;
                appendLog('error', t('massUpload.events.networkError', { filename: file.name, message: err?.message ?? String(err) }));
            }
        }

        setProgress(100);
        setStats({ ...totals });
        appendLog(
            totals.errors === 0 ? 'info' : 'error',
            t('massUpload.events.summary', {
                created: totals.created,
                updated: totals.updated,
                skipped: totals.skipped,
                errors: totals.errors,
                total: totals.total,
            }),
        );
        setRunning(false);
        setFinished(true);
    }, [running, files, appendLog, t]);

    if (permissions !== 'admin') {
        return (
            <Box p={3}>
                <Typography variant="h6" color="error">
                    {t('massUpload.permissionDenied')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Title title={t('massUpload.title')} />

            {/* ── Header card ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <CloudUploadIcon color="primary" fontSize="large" />
                        <Box>
                            <Typography variant="h5" fontWeight={600}>
                                {t('massUpload.heading')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('massUpload.descriptionPrefix')}{' '}
                                <code>&lt;sku&gt;.pdf</code> {t('massUpload.descriptionOr')}{' '}
                                <code>&lt;sku&gt;.epub</code>{t('massUpload.descriptionSuffix')}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Drop zone */}
                    <Box
                        onDrop={handleDrop}
                        onDragOver={e => e.preventDefault()}
                        onClick={() => !running && fileInputRef.current?.click()}
                        sx={{
                            border: '2px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: running ? 'not-allowed' : 'pointer',
                            backgroundColor: '#f0f7ff',
                            mb: 2,
                            opacity: running ? 0.6 : 1,
                            transition: 'background-color 0.2s',
                            '&:hover': { backgroundColor: running ? '#f0f7ff' : '#e3f2fd' },
                        }}
                    >
                        <FolderOpenIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body1" fontWeight={500}>
                            {files.length > 0
                                ? t('massUpload.dropZone.filesSelected', { count: files.length })
                                : t('massUpload.dropZone.placeholder')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('massUpload.dropZone.accepted')}
                        </Typography>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.epub"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                    </Box>

                    <ButtonField type="primary" size="large">
                        <button type="button" onClick={startUpload} disabled={running || files.length === 0}>
                            {running
                                ? t('massUpload.actions.uploadingProgress', { progress })
                                : files.length > 0
                                    ? t('massUpload.actions.uploadCount', { count: files.length })
                                    : t('massUpload.actions.upload')}
                        </button>
                    </ButtonField>
                </CardContent>
            </Card>

            {/* ── Progress bar ── */}
            {running && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
                </Box>
            )}

            {/* ── Stats chips ── */}
            {stats && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                            {t('massUpload.resultsTitle')}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Tag type="light" color="strong">{t('massUpload.stats.total', { value: stats.total })}</Tag>
                            <Tag type="light" color="success">{t('massUpload.stats.created', { value: stats.created })}</Tag>
                            <Tag type="light" color="info">{t('massUpload.stats.updated', { value: stats.updated })}</Tag>
                            <Tag type="light" color="warning">{t('massUpload.stats.skipped', { value: stats.skipped })}</Tag>
                            <Tag type="light" color={stats.errors > 0 ? 'danger' : 'strong'}>{t('massUpload.stats.errors', { value: stats.errors })}</Tag>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* ── Log ── */}
            {(log.length > 0 || running) && (
                <Card>
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('massUpload.progressTitle')}
                            </Typography>
                            {running && <CircularProgress size={18} />}
                            {finished && !running && <Tag type="light" color="success">{t('massUpload.completed')}</Tag>}
                        </Box>
                        <Paper
                            variant="outlined"
                            sx={{
                                maxHeight: 480,
                                overflowY: 'auto',
                                p: 1,
                                backgroundColor: '#fafafa',
                                fontFamily: 'monospace',
                                fontSize: '0.82rem',
                            }}
                        >
                            {log.map(entry => (
                                <Box
                                    key={entry.id}
                                    display="flex"
                                    alignItems="flex-start"
                                    gap={0.8}
                                    px={0.5}
                                    py={0.4}
                                    sx={{ backgroundColor: entryBg(entry.type), borderRadius: 0.5, mb: 0.2 }}
                                >
                                    <Box mt={0.1} flexShrink={0}>{entryIcon(entry.type)}</Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', fontSize: '0.82rem', wordBreak: 'break-word' }}
                                    >
                                        {entry.message}
                                    </Typography>
                                </Box>
                            ))}
                            <div ref={logEndRef} />
                        </Paper>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export const massUploadIcon = <CloudUploadIcon />;
